import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import { authGuard } from "./guards";
import AuthLayout from "@/layouts/AuthLayout.vue";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    component: AuthLayout,
    children: [
      {
        path: "",
        name: "Login",
        component: () => import("@/pages/auth/LoginPage.vue"),
        meta: { title: "로그인" },
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/layouts/AdminLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        redirect: "/notice",
      },
      {
        path: "notice",
        name: "NoticeList",
        component: () => import("@/pages/notice/NoticeListPage.vue"),
        meta: { title: "공지사항 목록", requiresAuth: true },
      },
      {
        path: "notice/new",
        name: "NoticeCreate",
        component: () => import("@/pages/notice/NoticeFormPage.vue"),
        meta: { title: "공지사항 등록", requiresAuth: true },
      },
      {
        path: "notice/:id",
        name: "NoticeDetail",
        component: () => import("@/pages/notice/NoticeDetailPage.vue"),
        meta: { title: "공지사항 상세", requiresAuth: true },
      },
      {
        path: "notice/:id/edit",
        name: "NoticeEdit",
        component: () => import("@/pages/notice/NoticeFormPage.vue"),
        meta: { title: "공지사항 수정", requiresAuth: true },
      },
      {
        path: "release-note",
        name: "ReleaseNoteList",
        component: () => import("@/pages/release-note/ReleaseNoteListPage.vue"),
        meta: { title: "릴리즈 노트 목록", requiresAuth: true },
      },
      {
        path: "release-note/new",
        name: "ReleaseNoteCreate",
        component: () => import("@/pages/release-note/ReleaseNoteFormPage.vue"),
        meta: { title: "릴리즈 노트 등록", requiresAuth: true },
      },
      {
        path: "release-note/:id",
        name: "ReleaseNoteDetail",
        component: () =>
          import("@/pages/release-note/ReleaseNoteDetailPage.vue"),
        meta: { title: "릴리즈 노트 상세", requiresAuth: true },
      },
      {
        path: "release-note/:id/edit",
        name: "ReleaseNoteEdit",
        component: () => import("@/pages/release-note/ReleaseNoteFormPage.vue"),
        meta: { title: "릴리즈 노트 수정", requiresAuth: true },
      },
      {
        path: "site-inquiry",
        name: "SiteInquiryList",
        component: () => import("@/pages/site-inquiry/SiteInquiryListPage.vue"),
        meta: { title: "현장 1:1 문의 목록", requiresAuth: true },
      },
      {
        path: "site-inquiry/new",
        name: "SiteInquiryCreate",
        component: () => import("@/pages/site-inquiry/SiteInquiryFormPage.vue"),
        meta: { title: "현장 1:1 문의 등록", requiresAuth: true },
      },
      {
        path: "site-inquiry/:id",
        name: "SiteInquiryDetail",
        component: () =>
          import("@/pages/site-inquiry/SiteInquiryDetailPage.vue"),
        meta: { title: "현장 1:1 문의 상세", requiresAuth: true },
      },
      {
        path: "site-inquiry/:id/edit",
        name: "SiteInquiryEdit",
        component: () => import("@/pages/site-inquiry/SiteInquiryFormPage.vue"),
        meta: { title: "현장 1:1 문의 수정", requiresAuth: true },
      },
      {
        path: "service-inquiry",
        name: "ServiceInquiryList",
        component: () =>
          import("@/pages/service-inquiry/ServiceInquiryListPage.vue"),
        meta: { title: "서비스 가입 문의", requiresAuth: true },
      },
      {
        path: "service-inquiry/:id",
        name: "ServiceInquiryDetail",
        component: () =>
          import("@/pages/service-inquiry/ServiceInquiryDetailPage.vue"),
        meta: { title: "서비스 가입 문의 상세", requiresAuth: true },
      },
      {
        path: "site",
        name: "SiteList",
        component: () => import("@/pages/site/SiteListPage.vue"),
        meta: { title: "현장 관리", requiresAuth: true },
      },
      {
        path: "site/new",
        name: "SiteCreate",
        component: () => import("@/pages/site/SiteCreatePage.vue"),
        meta: { title: "현장 등록", requiresAuth: true },
      },
      {
        path: "site/:id",
        name: "SiteDetail",
        component: () => import("@/pages/site/SiteDetailPage.vue"),
        meta: { title: "현장 상세", requiresAuth: true },
      },
      {
        path: "db-backup",
        name: "DbBackupStatus",
        component: () => import("@/pages/db-backup/DbBackupStatusPage.vue"),
        meta: { title: "DB 백업 상태", requiresAuth: true },
      },
      {
        path: "menu-access-stat",
        name: "MenuAccessStatList",
        component: () => import("@/pages/menu-access-stat/MenuUsagePage.vue"),
        meta: { title: "메뉴 사용 현황", requiresAuth: true },
      },
      // 예전 통계 화면 셋은 메뉴 사용 현황의 탭으로 합쳤다. 북마크·공유 링크가 깨지지 않게
      // 조건(쿼리)은 그대로 들고 해당 탭으로 넘긴다.
      {
        path: "menu-access-stat/sites",
        redirect: (to) => ({
          name: "MenuAccessStatList",
          query: { ...to.query, tab: "sites" },
        }),
      },
      {
        path: "menu-access-stat/mobile",
        redirect: (to) => ({
          name: "MenuAccessStatList",
          query: { ...to.query, platform: "mobile" },
        }),
      },
      {
        path: "menu-access-stat/v2",
        redirect: (to) => ({
          name: "MenuAccessStatList",
          query: { ...to.query, tab: "v2" },
        }),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 전역 가드 적용
router.beforeEach(authGuard);

// 페이지 타이틀 설정
router.afterEach((to) => {
  const title = to.meta.title as string;
  document.title = title ? `${title} - QBox 관리자` : "QBox 관리자";
});

export default router;
